import type { YearValue } from "@/lib/data/types";
import { averageOf } from "@/lib/stats";
import { FAIR_VALUE } from "@/lib/config";

export interface FairValuePoint {
  year: number;
  fairValue: number;
}

export interface FairValueSeriesResult {
  points: FairValuePoint[];
  averagePe: number;
  yearsUsed: number;
}

/**
 * Fairer-Wert-Verlauf: Jahres-EPS x durchschnittliches KGV der eigenen, verfuegbaren
 * Historie (siehe backlog.md -> Punkt 1) - bewusst kein fixer Branchenmultiplikator,
 * sondern ein aus den echten Daten DIESER Aktie abgeleiteter Durchschnitt. Reine
 * Funktion, kein I/O. Jahre mit negativem/fehlendem EPS liefern keinen Fair-Value-Punkt
 * (ein KGV auf einen Verlust anzuwenden waere bedeutungslos), Jahre mit negativem KGV
 * fliessen ebenfalls nicht in den Durchschnitt ein.
 */
export function computeFairValueSeries(
  pe: YearValue[],
  eps: YearValue[],
  minYears: number = FAIR_VALUE.minPeYears,
): FairValueSeriesResult | undefined {
  const validPe = pe.filter((p) => p.value > 0);
  if (validPe.length < minYears) return undefined;

  const averagePe = averageOf(validPe);
  if (averagePe === undefined || averagePe <= 0) return undefined;

  const points = eps
    .filter((e) => e.value > 0)
    .map((e) => ({ year: e.year, fairValue: e.value * averagePe }))
    .sort((a, b) => a.year - b.year);

  if (points.length === 0) return undefined;
  return { points, averagePe, yearsUsed: validPe.length };
}

/**
 * Niedrigster/hoechster valider historischer Multiplikator im gewaehlten
 * Bewertungszeitraum - Grundlage fuer die "konservative"/"optimistische" Hilfslinie im
 * Fairer-Wert-Simulator (Nutzer-Feedback 2026-07-18: bislang war beim Zwei-Punkte-Klick
 * IMMER nur der Kurs oder die eine Durchschnitts-Fair-Value-Linie waehlbar, unabhaengig
 * davon, wo im Chart geklickt wurde - eine zweite, datenbasierte Annahme fehlte als
 * Klickziel komplett). Gleiche Mindestanforderung wie computeFairValueSeries, damit
 * "genug Jahre vorhanden" fuer beide Funktionen konsistent bleibt. Reine Funktion, kein I/O.
 */
export function multipleRange(
  pe: YearValue[],
  minYears: number = FAIR_VALUE.minPeYears,
): { min: number; max: number } | undefined {
  const validValues = pe.filter((p) => p.value > 0).map((p) => p.value);
  if (validValues.length < minYears) return undefined;
  return { min: Math.min(...validValues), max: Math.max(...validValues) };
}

/**
 * Wie computeFairValueSeries, aber mit einem explizit uebergebenen Multiplikator statt
 * dessen Durchschnitt - Basis fuer die konservative/optimistische Hilfslinie (siehe
 * multipleRange). Reine Funktion, kein I/O.
 */
export function computeFairValueSeriesForMultiple(
  eps: YearValue[],
  multiple: number,
): FairValuePoint[] {
  if (multiple <= 0) return [];
  return eps
    .filter((e) => e.value > 0)
    .map((e) => ({ year: e.year, fairValue: e.value * multiple }))
    .sort((a, b) => a.year - b.year);
}

/**
 * Fair-Value-Bruecke fuers laufende Jahr auf TTM-Basis (Erik 2026-07-14: "2026 muss
 * unbedingt mit dem fairen Wert (KGV bereinigt) hinterlegt werden") - schliesst die
 * Luecke zwischen dem letzten testierten Jahresabschluss und der ersten
 * Analysten-Schaetzung, die je nach Aktie fehlen kann. Nutzt echte Trailing-Twelve-
 * Months-EPS (kein Analysten-Guess) mit derselben averagePe wie die historische Linie -
 * keine neue Bewertungsannahme. Liefert nur einen Punkt, wenn das laufende Jahr nicht
 * schon durch einen testierten Jahresabschluss abgedeckt ist. Reine Funktion, kein I/O.
 */
export function computeCurrentYearFairValue(
  trailingEps: number | undefined,
  averagePe: number,
  currentYear: number,
  lastHistoricalYear: number | undefined,
): FairValuePoint | undefined {
  if (trailingEps === undefined || trailingEps <= 0) return undefined;
  if (lastHistoricalYear !== undefined && currentYear <= lastHistoricalYear) return undefined;
  return { year: currentYear, fairValue: trailingEps * averagePe };
}

export interface FairValueForecastPoint {
  year: number;
  fairValue: number;
}

/**
 * Erweitert die Fair-Value-Linie in die Prognosezone (backlog.md -> §B.2), NUR fuer
 * Jahre, die tatsaechlich eine Analystenschaetzung haben (AnalystEstimates.perYear) -
 * keine erfundenen Schaetzjahre, oft nur 1-3 Jahre statt der vollen "bis zu 3"
 * Kapazitaet. `value` ist generisch (nicht nur EPS) - der Aufrufer entscheidet je
 * gewaehlter Bewertungskennzahl (backlog.md -> D1/Feature-2-Ergaenzung), was hineingereicht
 * wird (z.B. `p.eps` fuer "Gewinn je Aktie", eine abgeleitete Umsatz-je-Aktie-Schaetzung
 * fuer "Umsatz"). Reine Funktion, kein I/O.
 */
export interface ExtrapolatedFairValuePoint extends FairValueForecastPoint {
  source: "extrapolated";
}

/**
 * Verlaengert die Fair-Value-Prognose ueber die echten Analystenschaetzjahre hinaus
 * per Langfrist-Wachstumsrate (backlog.md -> [Punkt 51], "Prognosezeitraum auf 3 Jahre
 * verlaengern... auch wenn die Datengrundlage duenner wird, kleiner Disclaimer, dass
 * fortgeschriebene Analystenschaetzungen zugrunde liegen"). Bewusst als EIGENE Funktion
 * statt computeFairValueForecast() zu erweitern - haelt "echt" (Analystenschaetzung) und
 * "extrapoliert" strikt getrennte Typen/Arrays statt eines gemischten Ergebnisses mit
 * Discriminator-Feld, exakt dasselbe Prinzip wie computeForwardMultiplePath()
 * (lib/valuation/multiples.ts) fuer die KGV/KUV-Verlaufs-Prognose im Nachbar-Chart -
 * Konsumenten MUESSEN die Quelle visuell unterscheiden (gestrichelt statt durchgezogen),
 * nie unmarkiert vermischen. Ohne Wachstumsrate oder ohne einen letzten echten Punkt
 * bricht die Reihe ehrlich ab statt Jahre zu erfinden. Reine Funktion, kein I/O.
 */
export function extrapolateFairValueForecast(
  lastRealPoint: { year: number; value: number } | undefined,
  longTermGrowthRate: number | undefined,
  extraYears: number,
  averagePe: number,
): ExtrapolatedFairValuePoint[] {
  if (!lastRealPoint || longTermGrowthRate === undefined || longTermGrowthRate <= -1 || extraYears <= 0) {
    return [];
  }
  const points: ExtrapolatedFairValuePoint[] = [];
  let value = lastRealPoint.value;
  let year = lastRealPoint.year;
  for (let i = 0; i < extraYears; i++) {
    value *= 1 + longTermGrowthRate;
    year += 1;
    if (value <= 0) break;
    points.push({ year, fairValue: value * averagePe, source: "extrapolated" });
  }
  return points;
}

export function computeFairValueForecast(
  perYear: { year: number; value?: number }[],
  averagePe: number,
  lastHistoricalYear: number,
): FairValueForecastPoint[] {
  return perYear
    .filter((p) => p.year > lastHistoricalYear && p.value !== undefined && p.value > 0)
    .map((p) => ({ year: p.year, fairValue: p.value! * averagePe }))
    .sort((a, b) => a.year - b.year);
}

export interface FairValueForecastBandPoint {
  year: number;
  low: number;
  high: number;
}

/**
 * Wie `computeFairValueForecast`, aber fuer die untere/obere Schaetz-Bandbreite statt des
 * Mittelwerts (backlog.md -> D1/Feature-2-Ergaenzung "Prognosekorridor optimistisch/
 * pessimistisch") - aus Yahoos `earningsEstimate.low`/`.high` je Schaetzjahr, wo
 * verfuegbar. Nur Jahre mit BEIDEN Werten (low und high), keine erfundene Bandbreite aus
 * nur einem vorhandenen Wert. Reine Funktion, kein I/O.
 */
export function computeFairValueForecastBand(
  perYear: { year: number; low?: number; high?: number }[],
  averagePe: number,
  lastHistoricalYear: number,
): FairValueForecastBandPoint[] {
  return perYear
    .filter(
      (p) =>
        p.year > lastHistoricalYear &&
        p.low !== undefined &&
        p.high !== undefined &&
        p.low > 0 &&
        p.high > 0,
    )
    .map((p) => ({ year: p.year, low: p.low! * averagePe, high: p.high! * averagePe }))
    .sort((a, b) => a.year - b.year);
}

/**
 * Tage-genaue annualisierte Rendite zwischen zwei Zeitpunkten (backlog.md -> §B.6) -
 * gemeinsam fuer den Kauf-Simulator (§B.3/B.4) und spaeter C8 (Sparplan-Rechner)
 * nutzbar. Reine Funktion, kein I/O.
 */
export function annualizedReturn(
  startDate: Date,
  endDate: Date,
  startValue: number,
  endValue: number,
): number | undefined {
  if (startValue <= 0 || endValue < 0) return undefined;
  const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  if (days <= 0) return undefined;
  const years = days / 365.25;
  return Math.pow(endValue / startValue, 1 / years) - 1;
}

export interface IntervalEndpoint {
  /** Unix-Millisekunden. */
  time: number;
  /** Kurs (historische Punkte) oder Fair Value (Prognosezone). */
  value: number;
}

export interface IntervalPerformanceResult {
  startDate: Date;
  endDate: Date;
  startValue: number;
  endValue: number;
  priceChangePct: number;
  annualizedReturnPct: number | undefined;
  /** Ueber den Zeitraum anteilig (je Kalenderjahr nach Tagesanteil) aufsummierte Dividende je Aktie. */
  dividendPerShare: number;
  /** Dividendenrendite bezogen auf den Startwert des markierten Zeitraums, UEBER DEN
   * GESAMTEN Zeitraum kumuliert (nicht pro Jahr) - siehe personalDividendYieldOnCostAnnualized
   * fuer die auf ein Jahr umgerechnete, mit "Performance p. a." vergleichbare Rate. */
  personalDividendYieldOnCost: number;
  /** Wie personalDividendYieldOnCost, aber durch die verstrichenen Jahre geteilt (einfache
   * Durchschnittsbildung, keine Zinseszins-Kette - Dividenden sind ein laufender Cashflow,
   * kein einmalig verzinster Betrag). BUGFIX (2026-07-18): ohne diese Umrechnung zeigte ein
   * mehrjaehriger Zeitraum die kumulierte Gesamtrendite direkt neben "Performance p. a."
   * an, ohne das als "gesamter Zeitraum" statt "pro Jahr" zu kennzeichnen - las sich dadurch
   * wie eine absurd hohe jaehrliche Dividendenrendite (z.B. Amgen 2021-2026: ~15 % kumuliert
   * ueber knapp 5 Jahre, faelschlich als p.a.-Rate wahrgenommen). `undefined` bei einem
   * Zeitraum von null/negativen Jahren (kann bei den geprueften Eingaben praktisch nicht
   * vorkommen, a.time !== b.time ist bereits oben sichergestellt). */
  personalDividendYieldOnCostAnnualized: number | undefined;
}

/** Anteil eines Kalenderjahres, der innerhalb von [from, to] liegt (0..1). */
function yearOverlapFraction(year: number, from: Date, to: Date): number {
  const yearStart = Date.UTC(year, 0, 1);
  const yearEnd = Date.UTC(year + 1, 0, 1);
  const overlapStart = Math.max(yearStart, from.getTime());
  const overlapEnd = Math.min(yearEnd, to.getTime());
  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / (yearEnd - yearStart);
}

/**
 * Freie Zwei-Punkt-Simulation (backlog.md -> §B.4 / Master-Prompt 5): Performance,
 * Dividende und persoenliche Dividendenrendite zwischen zwei frei auf dem Chart
 * angeklickten Punkten - rueckwirkend auf dem echten Kursverlauf UND vorwaerts entlang
 * der Fair-Value-Prognoselinie, je nachdem welcher Endpunkt uebergeben wird (der
 * Aufrufer entscheidet Kurs vs. Fair Value je Punkt). Klickreihenfolge ist egal, die
 * Punkte werden hier nach Zeit sortiert. Dividende wird anteilig nach Tagesanteil je
 * Kalenderjahr verteilt statt volle Jahresbetraege auch fuer angeschnittene Rand-Jahre
 * anzurechnen (sonst Ueberzeichnung bei kurzen/verschobenen Zeitraeumen). Reine
 * Funktion, kein I/O.
 */
export function computeIntervalPerformance(
  a: IntervalEndpoint,
  b: IntervalEndpoint,
  dividendPerShare: YearValue[],
): IntervalPerformanceResult | undefined {
  if (a.time === b.time || a.value <= 0 || b.value < 0) return undefined;
  const [from, to] = a.time < b.time ? [a, b] : [b, a];
  const startDate = new Date(from.time);
  const endDate = new Date(to.time);

  const fromYear = startDate.getUTCFullYear();
  const toYear = endDate.getUTCFullYear();
  let dividend = 0;
  for (const d of dividendPerShare) {
    if (d.year < fromYear || d.year > toYear || d.value <= 0) continue;
    dividend += d.value * yearOverlapFraction(d.year, startDate, endDate);
  }

  const years = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  const personalDividendYieldOnCost = dividend / from.value;

  return {
    startDate,
    endDate,
    startValue: from.value,
    endValue: to.value,
    priceChangePct: (to.value - from.value) / from.value,
    annualizedReturnPct: annualizedReturn(startDate, endDate, from.value, to.value),
    dividendPerShare: dividend,
    personalDividendYieldOnCost,
    personalDividendYieldOnCostAnnualized: years > 0 ? personalDividendYieldOnCost / years : undefined,
  };
}

export interface PurchaseSimulationInput {
  amount: number;
  currentPrice: number;
  targetPrice: number;
  /** Erwartete Dividende je Aktie ueber den GESAMTEN Zeitraum (Summe, nicht jaehrlich). */
  expectedDividendPerShare: number;
  startDate: Date;
  endDate: Date;
}

export interface PurchaseSimulationResult {
  shares: number;
  priceGain: number;
  dividendIncome: number;
  total: number;
  totalReturnPct: number;
  annualizedReturnPct: number | undefined;
  /** Dividendenrendite bezogen auf den Einstandspreis (nicht den aktuellen Kurs), UEBER DEN
   * GESAMTEN Simulationszeitraum kumuliert - siehe personalDividendYieldOnCostAnnualized. */
  personalDividendYieldOnCost: number;
  /** Wie personalDividendYieldOnCost, auf ein Jahr umgerechnet - siehe Kommentar bei
   * IntervalPerformanceResult.personalDividendYieldOnCostAnnualized fuer den Bugfix-Kontext. */
  personalDividendYieldOnCostAnnualized: number | undefined;
}

/**
 * Rein rechnerische Wertentwicklungs-Simulation (backlog.md -> §B.3/B.4) - "Werkzeug
 * zum Denken", trifft keine Kauf-/Verkaufsaussage. Reine Funktion, kein I/O.
 */
export function simulatePurchase(input: PurchaseSimulationInput): PurchaseSimulationResult | undefined {
  if (input.currentPrice <= 0 || input.amount <= 0) return undefined;

  const shares = input.amount / input.currentPrice;
  const priceGain = shares * (input.targetPrice - input.currentPrice);
  const dividendIncome = shares * input.expectedDividendPerShare;
  const total = priceGain + dividendIncome;
  const totalReturnPct = total / input.amount;
  const annualizedReturnPct = annualizedReturn(
    input.startDate,
    input.endDate,
    input.amount,
    input.amount + total,
  );
  const personalDividendYieldOnCost = dividendIncome / input.amount;
  const years = (input.endDate.getTime() - input.startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

  return {
    shares,
    priceGain,
    dividendIncome,
    total,
    totalReturnPct,
    annualizedReturnPct,
    personalDividendYieldOnCost,
    personalDividendYieldOnCostAnnualized: years > 0 ? personalDividendYieldOnCost / years : undefined,
  };
}
